import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ContractGeneratorForm } from '@/components/contracts/ContractGeneratorForm';
import { ContractSection } from '@/types/contract.types';
import { generateContract } from '@/services/contracts/contractService';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const MSA_SECTIONS: ContractSection[] = [
  {
    id: 'term-termination',
    title: '1. Term & Termination',
    content: 'Term of Agreement and Termination Conditions...',
    isRequired: true,
    category: 'msa',
    order: 1
  },
  {
    id: 'services',
    title: '2. Services & Client Obligations',
    content: 'Scope of Services and Client Responsibilities...',
    isRequired: true,
    category: 'msa',
    order: 2
  },
  {
    id: 'fees',
    title: '3. Fees & Payment Terms',
    content: 'Service Fees, Payment Schedule, and Terms...',
    isRequired: true,
    category: 'msa',
    order: 3
  },
  {
    id: 'confidentiality',
    title: '4. Confidentiality',
    content: 'Confidentiality and Non-Disclosure Terms...',
    isRequired: true,
    category: 'msa',
    order: 4
  },
  {
    id: 'ip-rights',
    title: '5. Intellectual Property Rights',
    content: 'Ownership and Usage Rights...',
    isRequired: true,
    category: 'msa',
    order: 5
  }
];

const SOW_SECTIONS: ContractSection[] = [
  {
    id: 'project-overview',
    title: '1. Project Overview',
    content: 'Project scope and objectives...',
    isRequired: true,
    category: 'sow',
    order: 1
  },
  {
    id: 'deliverables',
    title: '2. Services & Deliverables',
    content: 'Detailed description of deliverables and timelines...',
    isRequired: true,
    category: 'sow',
    order: 2
  },
  {
    id: 'pricing',
    title: '3. Pricing & Payment Schedule',
    content: 'Service costs and payment milestones...',
    isRequired: true,
    category: 'sow',
    order: 3
  },
  {
    id: 'timeline',
    title: '4. Project Timeline',
    content: 'Project phases and milestone dates...',
    isRequired: true,
    category: 'sow',
    order: 4
  },
  {
    id: 'acceptance',
    title: '5. Acceptance Criteria',
    content: 'Deliverable acceptance and testing criteria...',
    isRequired: true,
    category: 'sow',
    order: 5
  }
];

export default function ContractGenerator() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id: organizationId } = useParams<{ id: string }>();
  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchOrganizationData() {
      if (!organizationId) {
        setError("No organization ID provided");
        setLoading(false);
        return;
      }

      try {
        // First fetch the organization data
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', organizationId)
          .single();

        if (orgError) throw orgError;

        // Then fetch the primary contact separately
        const { data: contactsData, error: contactsError } = await supabase
          .from('contacts')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_primary', true);

        if (contactsError) throw contactsError;

        // Combine the data
        const combinedData = {
          ...orgData,
          primaryContact: contactsData && contactsData.length > 0 ? contactsData[0] : null
        };

        setOrganization(combinedData);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchOrganizationData();
  }, [organizationId]);

  const handleGenerateContract = async (
    formData: any,
    msaSections: string[],
    sowSections: string[]
  ) => {
    try {
      if (!organizationId) throw new Error("Organization ID is required");
      
      // Convert contract value to number if it's a string
      const contractData = {
        ...formData,
        contract_value: typeof formData.contract_value === 'string' && formData.contract_value.trim() !== '' 
          ? parseFloat(formData.contract_value) 
          : 0
      };
      
      await generateContract(organizationId, contractData, msaSections, sowSections);
      
      toast({
        title: "Contracts Generated",
        description: "Your MSA and SOW have been generated successfully.",
      });
      
      navigate(`/organizations/${organizationId}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to generate contracts. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load organization data: {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const defaultValues = organization ? {
    organization_name: organization.name,
    client_name: organization.primaryContact?.first_name || '',
    client_title: organization.primaryContact?.job_title || '',
    start_date: organization.start_date || new Date().toISOString().split('T')[0],
    contract_value: organization.contract_value?.toString() || '',
    billing_contact: organization.primaryContact?.first_name || '',
    billing_email: organization.primaryContact?.email || '',
    billing_phone: organization.primaryContact?.phone || '',
    project_name: `Services for ${organization.name}`,
    term_length: "3 month",
    consultant_name: "Pablo Henderson",
    consultant_title: "President & CMO",
  } : undefined;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">Contract Generator</h1>
      
      <ContractGeneratorForm
        msaSections={MSA_SECTIONS}
        sowSections={SOW_SECTIONS}
        onSubmit={handleGenerateContract}
        defaultValues={defaultValues}
      />
    </div>
  );
}